import { IS_MOBILE, NAVBAR_WIDTH } from '@constants/global.js';
import { CollapsePreviewIcon, ExpandPreviewIcon } from '@icons/index.js';
import { resizingDrawer, resizingPreview } from '@signals/CommonSignals';
import { viewerResize } from '@signals/viewer';
import PreviewState from '@states/Preview.js';
import { UIButton, UIDiv } from '@ui';
import { animateValue } from '@utils/AnimationUtil.js';
import {
  FULLSCREEN_PRESETS,
  FullscreenManager,
} from '@utils/FullscreenManager.js';
import { getSharedProjectId } from '@utils/projectUtils';
import { Resizer } from '@utils/threeViewer/Resizer.js';
export default class Preview extends UIDiv {
  constructor() {
    super();
    this.setClass(
      'preview-container opacity-0 w-0 border-r border-gray-200 bg-white relative',
    );
    this.setId('preview-container');

    this.content = new UIDiv();
    this.content.setClass('preview-content h-full');

    this.collapseIndicator = new CollapsePreviewIcon({
      color: '#ffffff',
      size: 30,
    });
    this.collapseIndicator.setStyle('display', ['none']);
    this.collapseIndicator.setClass(
      'text-white p-4 rounded-xl flex items-center justify-center z-20',
    );
    this.expandIndicator = new ExpandPreviewIcon({
      color: '#ffffff',
      size: 30,
    });
    this.expandIndicator.setStyle('display', ['none']);
    this.expandIndicator.setClass(
      'text-white p-4 rounded-xl flex items-center justify-center z-20',
    );
    this.isProjectsPreview = false;

    this.currentWidth = 0;
    this.isFullScreen = false;
    this.currentActiveUrl = window.location.pathname;

    // Use shared fullscreen manager
    this.fullscreenManager = new FullscreenManager();

    this.expandButton = new UIButton('Expand');
    this.expandButton.setClass(
      'px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 absolute right-0 top-0',
    );
    // Initialize unified resizer
    this.resizer = new Resizer({
      position: 'right',
      minWidth: 400,
      maxWidth: window.innerWidth * 0.6,
      dragThreshold: 30,
      useIndicators: true,
      target: 'preview-container',
      getCurrentWidth: () => {
        if (
          typeof this.currentWidth !== 'number' ||
          isNaN(this.currentWidth) ||
          this.currentWidth <= 0
        ) {
          this.currentWidth = this.dom.offsetWidth || 400;
        }
        return this.currentWidth;
      },
      onCollapse: () => this.hide(),
      onExpand: () => this.expand('/projects'),
      onResize: newWidth => {
        if (typeof newWidth !== 'number' || isNaN(newWidth) || newWidth <= 0) {
          newWidth = this.dom.offsetWidth || 400;
        }
        this.updateFn(newWidth);
      },
      // appendTo removed
    });
    this.add(this.resizer);

    this.add(this.content);

    window.addEventListener('preview:shrink', async () => {
      this.isFullScreen ? await this.shrink() : null;
    });
    window.addEventListener('preview:expand', async () => {
      !this.isFullScreen ? await this.expand('/projects') : await this.shrink();
    });
    // Resize event listener
    window.addEventListener('resize', () => {
      // we want to update the width of the preview container only for the projects preview
      if (!this.isProjectsPreview) return;
      if (this.isFullScreen) {
        this.setStyle('width', [
          `calc(100vw - ${IS_MOBILE() ? 0 : NAVBAR_WIDTH()}px)`,
        ]);
        this.currentWidth =
          window.innerWidth - (IS_MOBILE() ? 0 : NAVBAR_WIDTH());
      } else {
        this.setStyle('width', [`${this.currentWidth}px`]);
      }
    });
    this.minWidth = 360;

    this.updateFn = currentWidth => {
      this.setStyle('width', [`${currentWidth}px`]);
      this.currentWidth = currentWidth;
      requestAnimationFrame(() => {
        viewerResize.dispatch();
        resizingPreview.dispatch(currentWidth);
      });
    };

    PreviewState.subscribe(() => {
      if (PreviewState.previewExpanded) {
        this.hide();
      }
    }).bind(this);

    resizingDrawer.add(width => {
      const availableWidth = window.innerWidth - width - NAVBAR_WIDTH();
      if (this.currentWidth < 400) return this.hide();
      if (this.currentWidth > availableWidth) {
        this.updateFn(availableWidth);
      }
    });
  }

  animate(start, end, duration, callback) {
    return animateValue(
      start,
      end,
      duration,
      currentWidth => this.updateFn(currentWidth),
      callback,
    );
  }

  show(component, expand, initialWidth = 420, startFullScreen = false) {
    if (this.isFullScreen)
      window.history.pushState('', '', this.currentActiveUrl);
    if (this.component?.constructor?.name !== component?.constructor?.name) {
      this.content.clear();
    }
    this.content.add(component);
    this.component = component;
    this.isProjectsPreview = component?.constructor?.name === 'ProjectsList';

    this.removeClass('opacity-0 hidden');
    if (startFullScreen) {
      this.isFullScreen = true;
      this.fullscreenManager.hideComponents(FULLSCREEN_PRESETS.DRAWER);
      this.resizer.addClass('hidden'); // Hide the resizer!

      PreviewState.setPreview({
        previewExpanded: true,
      });

      const targetWidth = IS_MOBILE()
        ? window.innerWidth
        : window.innerWidth - NAVBAR_WIDTH();
      this.setStyle('width', [
        IS_MOBILE() ? '100vw' : `calc(100vw - ${NAVBAR_WIDTH()}px)`,
      ]);
      this.currentWidth = targetWidth;
      return; // Exit early, no need for animation
    }
    animateValue(this.currentWidth, initialWidth, this.updateFn, () => {
      if (expand === false) {
        this.toggleClass('md:pb-[38px]', false);
        this.addClass('md:pb-[0px]');
      } else {
        this.removeClass('md:pb-[0px]');
        this.toggleClass('md:pb-[38px]', false);
      }
    });
    window.dispatchEvent(new CustomEvent('drawer:collapse'));
  }
  async hide() {
    PreviewState.setPreview({
      previewExpanded: false,
    });

    // Show hidden components when hiding
    this.fullscreenManager.showComponents();

    this.addClass('opacity-0');
    if (this.isFullScreen)
      window.history.pushState('', '', this.currentActiveUrl);
    this.isFullScreen = false;

    animateValue(this.currentWidth, 0, this.updateFn, () => {
      this.addClass('hidden');
    });
  }

  async expand(href) {
    this.isFullScreen = true;
    PreviewState.setPreview({
      previewExpanded: true,
    });

    // Hide other components before expanding
    this.fullscreenManager.hideComponents(FULLSCREEN_PRESETS.DRAWER);

    const url = new URL(href, window.location.origin);
    if (!url.search) {
      const projectId = getSharedProjectId();
      if (projectId) href += `?projectId=${projectId}`;
    }

    this.currentActiveUrl = window.location.pathname + window.location.search;
    window.history.pushState('', '', href);

    // Use consistent width calculation
    const targetWidth = IS_MOBILE()
      ? window.innerWidth
      : window.innerWidth - NAVBAR_WIDTH();

    await new Promise(resolve => {
      this.resizer.addClass('hidden');
      animateValue(this.currentWidth, targetWidth, this.updateFn, resolve);
    });
    this.setStyle('width', [
      `calc(100vw - ${IS_MOBILE() ? 0 : NAVBAR_WIDTH()}px)`,
    ]);
  }
  async shrink() {
    this.isFullScreen = false;
    PreviewState.setPreview({
      previewExpanded: false,
    });

    // Show hidden components when shrinking
    this.fullscreenManager.showComponents();

    await new Promise(resolve => {
      animateValue(this.currentWidth, 400, this.updateFn, () => {
        this.resizer.removeClass('hidden');
        resolve();
      });
    });
  }
}
